<template>
  <el-select
    class="aspect-ratio-selector"
    :class="{ compact }"
    popper-class="aspect-ratio-selector-popper"
    :value="value"
    :disabled="disabled"
    :aria-label="ariaLabel"
    @input="$emit('input', $event)"
    @change="$emit('change', $event)"
  >
    <template slot="prefix">
      <span class="aspect-ratio-selector__prefix" aria-hidden="true">
        <i :style="frameStyle(value, 16, 12)"></i>
      </span>
    </template>

    <el-option
      v-for="option in normalizedOptions"
      :key="option.value"
      :value="option.value"
      :label="`${option.value} · ${option.label}`"
    >
      <span class="aspect-ratio-selector__preview" aria-hidden="true">
        <i :style="frameStyle(option.value, 34, 24)"></i>
      </span>
      <strong>{{ option.value }}</strong>
      <small>{{ option.label }}</small>
    </el-option>
  </el-select>
</template>

<script>
const RATIO_LABELS = {
  '21:9': '超宽横版',
  '16:9': '宽幅横版',
  '3:2': '经典横版',
  '4:3': '标准横版',
  '5:4': '紧凑横版',
  '1:1': '方形',
  '4:5': '紧凑竖版',
  '3:4': '标准竖版',
  '2:3': '经典竖版',
  '9:16': '全屏竖版'
}

export default {
  name: 'AspectRatioSelector',
  props: {
    value: {
      type: String,
      default: '16:9'
    },
    options: {
      type: Array,
      default: () => ['16:9', '4:3', '1:1', '3:4', '9:16']
    },
    disabled: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: '选择图片画幅比例'
    }
  },
  computed: {
    normalizedOptions() {
      return this.options.map(option => {
        if (typeof option === 'string') {
          return { value: option, label: RATIO_LABELS[option] || '自定义比例' }
        }
        return {
          value: option.value,
          label: option.label || RATIO_LABELS[option.value] || '自定义比例'
        }
      })
    }
  },
  methods: {
    frameStyle(ratio, maxWidth, maxHeight) {
      const [rawWidth, rawHeight] = String(ratio || '1:1').split(':').map(Number)
      const width = Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : 1
      const height = Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : 1
      const scale = Math.min(maxWidth / width, maxHeight / height)
      return {
        width: `${Math.max(2, width * scale)}px`,
        height: `${Math.max(2, height * scale)}px`
      }
    }
  }
}
</script>

<style>
.aspect-ratio-selector { width: 176px; max-width: 100%; }
.aspect-ratio-selector.compact { width: 162px; }
.aspect-ratio-selector .el-input__inner { height: 34px; border-color: #d9e0db; border-radius: 6px; padding-left: 38px; background: #fff; color: #46534b; font-size: 11px; font-weight: 600; transition: border-color .18s ease, box-shadow .18s ease; }
.aspect-ratio-selector .el-input__inner:hover { border-color: #9eb5e4; }
.aspect-ratio-selector .el-input.is-focus .el-input__inner { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.aspect-ratio-selector .el-input__prefix { left: 11px; display: flex; align-items: center; color: #56625b; }
.aspect-ratio-selector .el-input__icon { line-height: 34px; }
.aspect-ratio-selector__prefix { width: 18px; height: 15px; display: grid; place-items: center; }
.aspect-ratio-selector__prefix i,
.aspect-ratio-selector__preview i { display: block; border: 2px solid currentColor; border-radius: 2px; background: rgba(37,99,235,.035); box-sizing: border-box; }
.aspect-ratio-selector-popper { min-width: 210px !important; border-color: #dfe5e1; border-radius: 8px; box-shadow: 0 14px 34px rgba(30,45,36,.14); }
.aspect-ratio-selector-popper .el-select-dropdown__list { padding: 5px; }
.aspect-ratio-selector-popper .el-select-dropdown__item { height: 46px; display: flex; align-items: center; gap: 10px; border-radius: 6px; padding: 0 11px; color: #4c5851; line-height: 1; }
.aspect-ratio-selector-popper .el-select-dropdown__item:hover { background: #f4f7fb; }
.aspect-ratio-selector-popper .el-select-dropdown__item.selected { background: #eef4ff; color: #1d4ed8; }
.aspect-ratio-selector__preview { width: 38px; height: 28px; display: grid; place-items: center; flex: 0 0 38px; }
.aspect-ratio-selector-popper strong { min-width: 32px; color: inherit; font-size: 12px; }
.aspect-ratio-selector-popper small { margin-left: auto; color: #7a857e; font-size: 10px; }
.aspect-ratio-selector-popper .selected small { color: #5577b5; }

@media (max-width: 767px) {
  .aspect-ratio-selector,
  .aspect-ratio-selector.compact { width: min(176px, 100%); }
  .aspect-ratio-selector-popper .el-select-dropdown__item { min-height: 44px; }
}
</style>
